import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import GeneralTablesCellsTdLink from 'lumen/components/general/tables/cells/td-link';
import ParityTablesCellsTdPlatform from 'lumen/components/parity/tables/cells/td-platform';
import ParityTablesCellsTdPromotionType from 'lumen/components/parity/tables/cells/td-promotion-type';
import ParityTablesCellsTdProof from 'lumen/components/parity/tables/cells/td-proof';
import ParityTablesCellsTdPushIssue from 'lumen/components/parity/tables/cells/td-push-issue';
import ParityTablesCellsTdStatusSelect from 'lumen/components/parity/tables/cells/td-status-select';
import type { ParityStrategyIssue } from 'lumen/models';
import type { IssueStatus } from 'lumen/models/parity-strategy/issue';

import styles from './table-parity-strategy-issues.module.css';

const COLUMNS = {
  FIRST_SPOT_CHECK: 'firstSpotCheckedArrivalDate',
  LAST_SPOT_CHECK: 'lastSpotCheckedArrivalDate',
  PROMOTION_TYPE: 'promotionType',
  CHANNEL: 'channel',
  PLATFORM: 'platform',
  // Disabled until supported by BE #PS-POS
  // POS: 'pos',
  LOS: 'los',
  STATUS: 'status',
  BRAND_RATE: 'brandRate',
  CHANNEL_RATE: 'channelRate',
  DISCOUNT: 'discount',
  PROOF: 'proof',
  PARITY_MANAGER: 'parityManager',
};

interface ParityTablesTableParityStrategyIssuesSignature {
  Args: {
    issues: ParityStrategyIssue[] | null;
    updateStatus: (issue: ParityStrategyIssue, status: IssueStatus) => void;
  };
}

export default class ParityTablesTableParityStrategyIssues extends Component<ParityTablesTableParityStrategyIssuesSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];
  @service('parity/logger')
  declare parityLogger: Services['parity/logger'];

  get columns(): CustomEmberTableColumn[] {
    const columns: CustomEmberTableColumn[] = [
      {
        name: this.intl.t('parity_strategy.columns.first_check'),
        valuePath: `${COLUMNS.FIRST_SPOT_CHECK}.value`,
        displayValuePath: `${COLUMNS.FIRST_SPOT_CHECK}.formattedValue`,
        textAlign: 'left',
      },
      {
        name: this.intl.t('parity_strategy.columns.last_check'),
        valuePath: `${COLUMNS.LAST_SPOT_CHECK}.value`,
        displayValuePath: `${COLUMNS.LAST_SPOT_CHECK}.formattedValue`,
        textAlign: 'left',
      },
      {
        name: this.intl.t('parity.channel'),
        valuePath: COLUMNS.CHANNEL,
        textAlign: 'left',
        tdComponent: GeneralTablesCellsTdLink,
        isSortable: false,
      },
      {
        name: this.intl.t('general.type'),
        valuePath: COLUMNS.PROMOTION_TYPE,
        textAlign: 'left',
        tdComponent: ParityTablesCellsTdPromotionType,
        isSortable: false,
      },
      {
        name: this.intl.t('general.platform.plural', { count: 1 }),
        valuePath: COLUMNS.PLATFORM,
        textAlign: 'center',
        tdComponent: ParityTablesCellsTdPlatform,
        isSortable: false,
      },
      // Disabled until supported by BE #PS-POS
      // {
      //   name: this.intl.t('general.pos_short'),
      //   valuePath: COLUMNS.POS,
      //   textAlign: 'left',
      //   tdComponent: TdPosCountry,
      //   isSortable: false,
      // },
      {
        name: this.intl.t('rates.los'),
        valuePath: COLUMNS.LOS,
        textAlign: 'left',
        isSortable: false,
      },
      {
        name: this.intl.t('general.status'),
        valuePath: COLUMNS.STATUS,
        width: 250,
        textAlign: 'left',
        tdComponent: ParityTablesCellsTdStatusSelect,
        isSortable: false,
      },
      {
        name: this.intl.t('parity.base_rate'),
        valuePath: `${COLUMNS.BRAND_RATE}.value`,
        displayValuePath: COLUMNS.BRAND_RATE,
        textAlign: 'right',
        tdComponent: GeneralTablesCellsTdLink,
      },
      {
        name: this.intl.t('parity.channel_rate'),
        valuePath: `${COLUMNS.CHANNEL_RATE}.value`,
        displayValuePath: COLUMNS.CHANNEL_RATE,
        textAlign: 'right',
        tdComponent: GeneralTablesCellsTdLink,
      },
      {
        name: this.intl.t('general.discount.plural', { count: 1 }),
        valuePath: `${COLUMNS.DISCOUNT}.value`,
        displayValuePath: `${COLUMNS.DISCOUNT}.formattedValue`,
        textAlign: 'right',
        tdClassNames: 'text-critical-default tabular-nums',
      },
      {
        name: this.intl.t('parity.proof'),
        valuePath: COLUMNS.PROOF,
        textAlign: 'left',
        tdComponent: ParityTablesCellsTdProof,
        isSortable: false,
      },
    ];

    if (this.access.hasParityManagerAccess) {
      columns.push({
        name: this.intl.t('menu.parity_manager'),
        valuePath: COLUMNS.PARITY_MANAGER,
        textAlign: 'left',
        tdComponent: ParityTablesCellsTdPushIssue,
        isSortable: false,
      });
    }

    return columns;
  }

  get rows(): CustomEmberTableRow[] {
    if (!this.args.issues) {
      return [];
    }

    return this.args.issues?.map((issue) => {
      return {
        [COLUMNS.FIRST_SPOT_CHECK]: {
          formattedValue: issue.formattedFirstSpotCheckedArrivalDate,
          value: issue.firstSpotCheckedArrivalDate.unix(),
        },
        [COLUMNS.LAST_SPOT_CHECK]: {
          formattedValue: issue.formattedLastSpotCheckedArrivalDate,
          value: issue.lastSpotCheckedArrivalDate.unix(),
        },
        [COLUMNS.PROMOTION_TYPE]: issue,
        [COLUMNS.CHANNEL]: {
          formattedValue: this.ota.otaIdToPrettyName(issue.channel),
          redirectUrl: issue.extranetUrl,
        },
        [COLUMNS.PLATFORM]: issue.platform,
        // Disabled until supported by BE #PS-POS
        // [COLUMNS.POS]: issue.pos,
        [COLUMNS.LOS]: issue.formattedLos,
        [COLUMNS.STATUS]: {
          issue,
          value: issue.status,
          updateStatus: this.args.updateStatus,
        },
        [COLUMNS.BRAND_RATE]: {
          formattedValue: issue.formattedBrandRate,
          redirectUrl: issue.brandRateUrl,
          value: issue.brandRate,
        },
        [COLUMNS.CHANNEL_RATE]: {
          formattedValue: issue.formattedChannelRate,
          redirectUrl: issue.channelRateUrl,
          value: issue.channelRate,
        },
        [COLUMNS.DISCOUNT]: {
          formattedValue: issue.formattedDiscount,
          value: issue.discountPercent,
        },
        [COLUMNS.PROOF]: {
          formattedValue: issue.formattedValue,
          redirectUrl: issue.screenshotUrl,
          isScreenshotSupported: issue.isScreenshotSupported,
          onDoubleClick: () => this.parityLogger.logParityStrategyIssue(issue),
        },
        [COLUMNS.PARITY_MANAGER]: issue,
      };
    });
  }

  <template>
    <div class={{styles.container}}>
      <PrismTable
        @columns={{this.columns}}
        @enableAutoSortsUpdate={{true}}
        @highlightTitleColumn={{false}}
        @rows={{this.rows}}
      />
    </div>
  </template>
}
